import React, { useEffect, useMemo, useState } from 'react';
import PageShell from './PageShell';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';

type MarketLike = string | { code?: string; name?: string };

function MetrcAuthDialog({ markets = [], onClose, onAuthenticate }: { markets?: MarketLike[]; onClose: () => void; onAuthenticate: ({ market, username, password }: { market: string; username: string; password: string }) => void }) {
  const marketOptions = useMemo(() => {
    const byValue = new Map<string, { value: string; label: string }>();
    (markets || []).forEach((m) => {
      if (!m) return;
      if (typeof m === 'string') {
        const value = String(m);
        if (!value) return;
        if (!byValue.has(value)) byValue.set(value, { value, label: value });
        return;
      }

      const code = (m.code ? String(m.code) : '').trim();
      const name = (m.name ? String(m.name) : '').trim();
      const value = code || name;
      if (!value) return;
      const label = code && name ? `${code} — ${name}` : (code || name);
      if (!byValue.has(value)) byValue.set(value, { value, label });
    });

    return Array.from(byValue.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [markets]);

  const [market, setMarket] = useState(marketOptions[0]?.value || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!marketOptions.length) {
      if (market) setMarket('');
      return;
    }

    const exists = marketOptions.some((o) => o.value === market);
    if (!exists) setMarket(marketOptions[0].value);
  }, [marketOptions]);

  const validate = () => {
    const next: Record<string, any> = {};
    if (!market) next.market = 'Market is required';
    if (!username) next.username = 'Username is required';
    if (!password) next.password = 'Password is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleAuth = () => {
    if (!validate()) return;
    onAuthenticate({ market, username, password });
  };

  const footer = (
    <DialogFooter>
      <button className="button" onClick={handleAuth} data-testid="metrc-auth-submit">Authenticate &amp; Fetch</button>
    </DialogFooter>
  );

  return (
    <PageShell
      contextLabel="Integrations"
      title="Authenticate"
      onClose={onClose}
      framed={false}
      testId="metrc-auth"
      preview={(
        <div className="card" style={{ padding: 16 }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Metrc Connection</div>
          <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6 }}>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Market:</span> {market || '—'}</div>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Username:</span> {username || '—'}</div>
            <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Password:</span> {password ? '••••••••' : '—'}</div>
          </div>
          <div className="divider" style={{ margin: '12px 0' }} />
          <div className="muted" style={{ fontSize: 12 }}>Authenticate to fetch facilities and products for import.</div>
        </div>
      )}
      alert={<DialogAlert show={Object.keys(errors).length > 0} />}
      footer={footer}
    >
      <div className="form-grid">
        <div className="field full-span">
          <label className="label">Market <span className="required-dot" aria-label="required">*</span></label>
          <select
            className={`select ${errors.market ? 'error' : ''}`}
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            data-testid="metrc-auth-market"
          >
            <option value="">Select market</option>
            {[...marketOptions]
              .sort((a, b) => (a.label || '').localeCompare((b.label || ''), undefined, { sensitivity: 'base' }))
              .map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {errors.market && <div className="error-text">{errors.market}</div>}
        </div>

        <div className="field full-span">
          <label className="label">Metrc Username <span className="required-dot" aria-label="required">*</span></label>
          <input
            className={`input ${errors.username ? 'error' : ''}`}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter Metrc username"
            data-testid="metrc-auth-username"
          />
          {errors.username && <div className="error-text">{errors.username}</div>}
        </div>

        <div className="field full-span">
          <label className="label">Metrc Password <span className="required-dot" aria-label="required">*</span></label>
          <input
            type="password"
            className={`input ${errors.password ? 'error' : ''}`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter Metrc password"
            data-testid="metrc-auth-password"
          />
          {errors.password && <div className="error-text">{errors.password}</div>}
        </div>
      </div>
    </PageShell>
  );
}

export default React.memo(MetrcAuthDialog);